import React from 'react';
import {
  Action, Badge, Breadcrumb, Card, CardSkeleton, Grid, HStack, ICON_EYE_OUTLINE, ICON_IMAGE_SEARCH_OUTLINE, Message, Select, Spacing, Stack,
  Text,
} from '@doveit/bricks';
import { useNavigate } from 'react-router-dom';
import { useMarketingFolderItemsInfinite } from '../../hooks/use-marketing-folder-items/useMarketingFolderItems';
import { AVAILABLE_FOLDERS_BY_USER_ROLE, canvaResourceTypeLabels, CANVA_URL } from '../../constants';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { goToPage } from '../../../utils/navigate/utils';
import useMarketingFolder from '../../hooks/use-marketing-folder/useMarketingFolder';
import useFilters from '../../../hooks/use-filters/useFilters';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useCurrentAgentSync } from '../../../hooks/use-agent/useAgent';
import CanvaCard from '../../components/canva-card/CanvaCard';
import ExportMarketingDesignAction from '../../containers/export-marketing-design-action/ExportMarketingDesignAction';

const RESOURCE_TYPE_FILTERS = ['design', 'brand_template'] as const;

type FolderCrumb = { id: string; name: string };

const parsePath = (path: string | undefined): FolderCrumb[] => {
  if (!path) return [];

  return path.split(',').map((crumb) => {
    const [id, ...nameParts] = crumb.split(':');
    return { id, name: nameParts.join(':') };
  });
};

const parseItemTypes = (raw: string | undefined): Set<typeof RESOURCE_TYPE_FILTERS[number]> => {
  if (!raw) return new Set(RESOURCE_TYPE_FILTERS);
  return new Set(raw.split(',') as typeof RESOURCE_TYPE_FILTERS[number][]);
};

const serializePath = (crumbs: FolderCrumb[]): string => crumbs.map((crumb) => `${crumb.id}:${crumb.name}`).join(',');

const ExploreCanvaResourcesPage: React.FC = () => {
  const isMobile = useIsDevice('mobile');
  const navigate = useNavigate();
  const { userIsMarketing } = useRBAC();
  const { data: currentAgent } = useCurrentAgentSync();

  const roleConfig = React.useMemo(() => {
    if (userIsMarketing) return AVAILABLE_FOLDERS_BY_USER_ROLE.marketing;
    if (currentAgent?.configuration?.marketing?.isCanvaAccessEnabled) return AVAILABLE_FOLDERS_BY_USER_ROLE.agent;

    return AVAILABLE_FOLDERS_BY_USER_ROLE.default;
  }, [currentAgent?.configuration?.marketing?.isCanvaAccessEnabled, userIsMarketing]);

  const {
    filters: { folderId = roleConfig.default, path, itemTypes },
    replaceFilters,
  } = useFilters<{ folderId?: string; path?: string; itemTypes?: string }>();

  const {
    data: folder,
    isLoading: isFolderLoading,
    error: folderError,
  } = useMarketingFolder(folderId);

  const parsedPath = React.useMemo(() => parsePath(path), [path]);
  const parsedItemTypes = React.useMemo(() => parseItemTypes(itemTypes), [itemTypes]);

  const toggleTypeFilter = React.useCallback((filter: typeof RESOURCE_TYPE_FILTERS[number]) => {
    const next = new Set(parsedItemTypes);
    if (next.has(filter)) {
      if (next.size === 1) return;
      next.delete(filter);
    } else {
      next.add(filter);
    }
    replaceFilters({ itemTypes: [...next].join(',') });
  }, [parsedItemTypes, replaceFilters]);

  const {
    data: items,
    isLoading: areItemsLoading,
    error: itemsError,
    hasMore: hasMoreItems,
    loadMore: loadMoreItems,
    isLoadingMore: areMoreItemsLoading,
  } = useMarketingFolderItemsInfinite((folderId && folder) ? {
    folderId,
    itemType: ['folder', ...parsedItemTypes],
  } : undefined);

  const rootFolderId = React.useMemo(
    () => (parsedPath.length > 0 ? parsedPath[0].id : folderId),
    [parsedPath, folderId],
  );

  const breadcrumbItems = React.useMemo(() => [
    ...parsedPath.map((crumb, index) => ({
      label: crumb.name,
      onClick: () => replaceFilters({
        folderId: crumb.id,
        path: serializePath(parsedPath.slice(0, index)),
      }),
    })),
    { label: folder?.name ?? '...' },
  ], [parsedPath, folder, replaceFilters]);

  const handleFolderOpen = React.useCallback((subfolderId: string) => {
    replaceFilters({
      folderId: subfolderId,
      path: serializePath([
        ...parsedPath,
        { id: folderId, name: folder?.name ?? folderId },
      ]),
    });
  }, [parsedPath, folderId, folder, replaceFilters]);

  if (folderError || itemsError) {
    return (
      <SimplePageLayout>
        <Card paddingSize="M">
          <Card.Header
            icon={{ path: ICON_IMAGE_SEARCH_OUTLINE }}
            title="Esplora risorse Canva"
          />

          <Card.Content>
            <Message
              message="Non è stato possibile recuperare le risorse Canva"
              type="critical"
              actions={[
                <Action
                  aria-label="Visualizza le risorse su Canva"
                  label="Visualizza su Canva"
                  color="critical"
                  emphasis="high"
                  size="S"
                  onClick={() => goToPage(CANVA_URL)}
                />,
              ]}
            >
              <Text.BodySmall color="critical.default.low">
                Prova ad aggiornare la pagina, o visualizzale direttamente su Canva.
              </Text.BodySmall>
            </Message>
          </Card.Content>
        </Card>
      </SimplePageLayout>
    );
  }

  return (
    <SimplePageLayout>
      <Card paddingSize="M">
        <Card.Header
          icon={{ path: ICON_IMAGE_SEARCH_OUTLINE }}
          title="Esplora risorse Canva"
        />

        <Card.Content>
          <Stack gap={300}>
            <Text.Body>
              In questa sezione trovi le risorse Canva organizzate per cartelle.
            </Text.Body>

            {roleConfig.available.length > 1 && (
              <Select
                width={isMobile ? '100%' : '200px'}
                value={rootFolderId}
                aria-label="Seleziona un una cartella"
                options={roleConfig.available.map((availableFolder) => ({
                  label: availableFolder.name,
                  value: availableFolder.id,
                }))}
                size="S"
                onChange={({ target }) => replaceFilters({ folderId: target.value, path: undefined })}
              />
            )}
          </Stack>
        </Card.Content>

        {breadcrumbItems.length > 1 && (
          <>
            <Card.Divider />

            <Card.Content paddingSize="XS">
              <Spacing padding={[0, 150]}>
                <Breadcrumb items={breadcrumbItems} />
              </Spacing>
            </Card.Content>

            <Card.Divider />
          </>
        )}

        <Card.Content>
          <Stack gap={300} wrap="nowrap">
            <HStack gap={75} aria-label="Filtri risorsa">
              {RESOURCE_TYPE_FILTERS.map((filter) => (
                <Badge
                  key={filter}
                  label={canvaResourceTypeLabels[filter].plural}
                  color={parsedItemTypes.has(filter) ? 'primary' : 'neutral'}
                  emphasis="low"
                  onClick={() => toggleTypeFilter(filter)}
                />
              ))}
            </HStack>

            {(isFolderLoading || areItemsLoading || areMoreItemsLoading) && (
              <Grid gutter={150} aria-label="Risorse Canva in caricamento">
                {Array.from({ length: 10 }).map((_, i) => (
                  <Grid.Unit
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    size={{
                      XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                    }}
                  >
                    <CardSkeleton />
                  </Grid.Unit>
                ))}
              </Grid>
            )}

            {!isFolderLoading && !areItemsLoading && !areMoreItemsLoading && items.length === 0 && (
              <Message message="Nessuna risorsa Canva trovata" />
            )}

            {items.length > 0 && (
              <Grid gutter={150} aria-label="Lista risorse Canva">
                {items.map((item) => {
                  switch (item.type) {
                    case 'design':
                      return (
                        <Grid.Unit
                          key={`design-${item.design.id}`}
                          size={{
                            XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                          }}
                        >
                          <CanvaCard
                            title={item.design.title}
                            date={item.design.updatedAt}
                            dateLabel="Aggiornato il"
                            type="design"
                            thumbnailUrl={item.design.thumbnail?.url}
                            primaryAction={(
                              <ExportMarketingDesignAction design={item.design} />
                            )}
                            secondaryAction={{
                              icon: { path: ICON_EYE_OUTLINE },
                              label: 'Visualizza Design',
                              onClick: () => goToPage(item.design.viewUrl),
                            }}
                          />
                        </Grid.Unit>
                      );

                    case 'brand_template':
                      return (
                        <Grid.Unit
                          key={`brand_template-${item.brandTemplate.id}`}
                          size={{
                            XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                          }}
                        >
                          <CanvaCard
                            title={item.brandTemplate.title}
                            date={item.brandTemplate.createdAt}
                            dateLabel="Creato il"
                            type="template"
                            thumbnailUrl={item.brandTemplate.thumbnail?.url}
                            primaryAction={(
                              <Action
                                label="Usa template"
                                size="S"
                                onClick={() => navigate(`/marketing/canva/brand-templates/${item.brandTemplate.id}/fill`)}
                              />
                            )}
                            secondaryAction={{
                              icon: { path: ICON_EYE_OUTLINE },
                              label: 'Visualizza template',
                              onClick: () => goToPage(item.brandTemplate.viewUrl),
                            }}
                          />
                        </Grid.Unit>
                      );
                    case 'folder':
                      return (
                        <Grid.Unit
                          key={`folder-${item.folder.id}`}
                          size={{
                            XS: 1, SM: 1 / 2, MD: 1 / 3, LG: 1 / 4, XL: 1 / 5,
                          }}
                        >
                          <CanvaCard
                            title={item.folder.name}
                            type="folder"
                            primaryAction={(
                              <Action
                                label="Apri cartella"
                                size="S"
                                color="primary"
                                onClick={() => handleFolderOpen(item.folder.id)}
                              />
                            )}
                          />
                        </Grid.Unit>
                      );
                    default:
                      return null;
                  }
                })}
              </Grid>
            )}

            {hasMoreItems && (
              <Stack alignItems="center">
                <Action
                  label="Carica altre"
                  aria-label="Carica altre risorse Canva"
                  onClick={loadMoreItems}
                  loading={areMoreItemsLoading}
                  size={isMobile ? 'S' : 'M'}
                />
              </Stack>
            )}
          </Stack>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default ExploreCanvaResourcesPage;
